import type { FC, ReactNode } from 'react'
import { Box } from '@mui/material'
import type { Tones } from '~/shared/lib/theme'

export type Tone = keyof Tones

interface StatusPillProps {
  tone: Tone
  label: ReactNode
  /** Small leading icon; decorative (the label carries the meaning). */
  icon?: ReactNode
  /** Leading status dot in the tone's foreground color. */
  dot?: boolean
  'data-testid'?: string
}

/**
 * Tone-colored rounded-full status chip (case status, form status, network
 * status, determination). Colors come from `theme.palette.tones`, so it reads
 * its colors from the theme rather than hardcoding.
 */
export const StatusPill: FC<StatusPillProps> = ({ tone, label, icon, dot, ...rest }) => (
  <Box
    component="span"
    sx={theme => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.625,
      px: 1.25,
      py: 0.375,
      borderRadius: 999,
      bgcolor: theme.palette.tones[tone].bg,
      color: theme.palette.tones[tone].fg,
      fontSize: '1.2rem',
      fontWeight: 600,
      lineHeight: 1.5,
      whiteSpace: 'nowrap',
    })}
    {...rest}
  >
    {dot && (
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'currentColor',
          flex: '0 0 auto',
        }}
      />
    )}
    {icon && (
      <Box
        component="span"
        aria-hidden
        sx={{ display: 'inline-flex', fontSize: '1.4rem', '& svg': { fontSize: 'inherit' } }}
      >
        {icon}
      </Box>
    )}
    {label}
  </Box>
)
